<template>
    <div>
        <b-overlay
            :show="page_answer_loading"
            rounded="sm"
            blur="0"
            opacity="0.5"
            z-index="9999"
        >
            <!-- 답변 전체 인쇄/다운로드 -->
            <b-container
                class="text-right position-relative"
            >
                <AuditPrintButtonV2 v-if="answer_company?.company_id && !show_ai_assessment"
                                    btn_variant="subtop"
                                    :d_none="is_printing === true ? 'd-none' : ''"
                                    @modal-opened="is_printing = true"
                                    @modal-closed="is_printing = false"
                >
                    답변 전체 인쇄/다운로드
                    <fa icon="print" class="ml-2"></fa>
                </AuditPrintButtonV2>
            </b-container>

            <!-- 기업 Answer & Audit 화면 -->
            <b-container :class="[answer_audit_mode !== 'conclusion' ? 'm-0 px-2 px-md-5' : 'm-0 px-3 pl-md-5', {'ai-review-page': show_ai_assessment}]" fluid>
                <b-button v-show="!show_ai_assessment" class="mr-2" variant="dark-border" @click="returnToSelect">다른 기업 선택</b-button>
                <b-button-group v-if="is_admin >= 3 && !show_ai_assessment">
                    <b-button :variant="answer_audit_mode === 'evaluation' ? 'dark' : 'dark-border'"
                              @click="changeMode('evaluation')"
                    >
                        상세평가
                    </b-button>
                    <b-button :variant="answer_audit_mode === 'conclusion' ? 'dark' : 'dark-border'"
                              @click="changeMode('conclusion')"
                    >
                        종합의견
                    </b-button>
                </b-button-group>
                <div v-if="answer_audit_mode === 'conclusion' && !show_ai_assessment"
                     class="blue f-90 fw-500"
                >
                    <fa icon="exclamation-circle"></fa>
                    종합의견은 결과보고서에 반영됩니다.
                </div>

                <div v-show="!show_ai_assessment" id="company-info-popover"
                     class="text-center mt-4 pb-5 gray-600"
                     :style="answer_audit_mode === 'conclusion' ? 'padding-right:2rem;' : ''"
                >
                    <template v-if="answer_company?.company_id">
                        <div class="mb-3">
                            {{
                                admin_mode === 'project' ? answer_company.campaign_name + ' / ' + answer_company.campaign_id : answer_company.project_code
                            }}
                        </div>
                        <h3> {{ answer_company.company_name }} / {{ answer_company.company_id }} </h3>
                    </template>

                </div>

                <!-- company_info -->
                <b-popover v-if="!show_ai_assessment" target="company-info-popover" triggers="hover" placement="top">
                    사업자번호: {{ company_info.code }} <br>
                    임직원 수: {{ company_info.employee }}명
                </b-popover>

                <!-- 점수 요약 422: 문항 안 해당없음이 섞이면 백엔드가 일부러 멈춘다 — 자동 보정하지 않고 막힌 문항만 안내.
                     상세평가 헤더(평가 요약 카드)는 position-absolute 라 그 안에 두면 첫 문항 평가 패널(margin-top 65px 고정)에 가려진다 -->
                <b-alert :show="category3IntegrityIssues != null && !show_ai_assessment" variant="danger" class="f-90">
                    업체 답변과 평가자 판정의 "해당없음"이 서로 다른 문항이 있어 점수를 계산할 수 없습니다.
                    해당 문항에서 문항 단위로 해당없음/해당있음 처리하거나 초기화해 주세요.
                    <ul v-if="category3IntegrityIssues?.length" class="mb-0 mt-1">
                        <li v-for="issue in category3IntegrityIssues"
                            :key="`${issue.campaign_id}-${issue.sub_code}`"
                        >
                            {{ issue.label || issue.sub_code }} ({{ issue.sub_code }})
                        </li>
                    </ul>
                </b-alert>

                <b-row class="d-flex align-items-stretch ">
                    <b-col
                        :class="[
                            show_ai_assessment ? 'ai-question-sidebar' : (answer_audit_mode === 'conclusion' ? 'col-2' : 'col-3 col-xl-2'),
                            {'is-compact': show_ai_assessment && aiQuestionSidebarCollapsed},
                            'bgray-100 py-3',
                        ]"
                    >
                        <div class="position-sticky questions-sticky">
                            <div :class="['questions-heading', {'is-compact': aiQuestionSidebarCollapsed}]">
                                <h5 v-if="!show_ai_assessment || !aiQuestionSidebarCollapsed" class="mb-0 gray-500">Questions</h5>
                                <b-button
                                    v-if="show_ai_assessment"
                                    size="sm"
                                    variant="link"
                                    class="questions-collapse-button"
                                    :title="aiQuestionSidebarCollapsed ? 'Questions 펼치기' : 'Questions 접기'"
                                    :aria-label="aiQuestionSidebarCollapsed ? 'Questions 펼치기' : 'Questions 접기'"
                                    @click="aiQuestionSidebarCollapsed = !aiQuestionSidebarCollapsed"
                                >
                                    <fa :icon="aiQuestionSidebarCollapsed ? 'angle-double-right' : 'angle-double-left'"/>
                                </b-button>
                            </div>
                            <audit-question-list
                                :compact="show_ai_assessment && aiQuestionSidebarCollapsed"
                                @reset-sub-evaluation="resetSubEvaluation"
                                @taxonomy="showOrHideTaxonomy"
                            >
                            </audit-question-list>
                        </div>
                        <hr v-if="!show_ai_assessment || !aiQuestionSidebarCollapsed" class="border-0 bottom-space">
                    </b-col>
                    <b-col
                        :class="[show_ai_assessment ? 'ai-review-host ai-review-main' : (answer_audit_mode === 'evaluation' ? 'col-9 col-xl-10' : 'narrowed bgray-100'), 'evaluation-panel-1 position-relative', show_ai_assessment ? 'p-0' : 'py-3']"
                        :style="wide_answer_view === true ? 'background-color:#F8F8F8;' : ''"
                    >
                        <h5 v-if="!show_ai_assessment" class="pb-2 mb-3 border-bottom-gray-bold gray-500"
                            :style="answer_audit_mode !== 'comment' ? 'width:calc(50% - 15px)' : 'width:100%'"
                        > Answers
                            <template
                                v-if="is_admin >= 3 && ($store.state.question_composition.question_code !== '' || taxonomy_mode === true) && sub_ev_error_data == null">
                                <b-button v-b-modal.company-attachements-storage
                                          variant="dark-border" size="sm"
                                          class="ml-1 mr-1"
                                >
                                    <fa icon="user-plus" class="mr-1"></fa>
                                    {{ $t('첨부파일 보관함') }}
                                </b-button>
                                <b-button variant="dark-border" size="sm"
                                          @click="toggleAllowedEdit"
                                >
                                    {{ is_allowed_edit_msg }}
                                </b-button>
                            </template>
                        </h5>

                        <template v-if="is_admin >= 3 && !show_ai_assessment">
                            <div class="position-absolute d-flex align-items-end" style="top:0; right:0; z-index:200"
                                 :class="{'ai-assessment-on': show_ai_assessment }"
                            >
                                <div v-if="show_ai_assessment"
                                     v-b-tooltip.hover
                                     class="ai-refresh-btn"
                                     :title="$t('현재 질문의 전체 AI 평가 데이터 \n새로 불러오기')"
                                     @click="fetchAiEvDataWrap"
                                >
                                    <fa icon="sync-alt"></fa>
                                </div>
                                <div v-if="is_selected_question" class="d-flex align-items-center mr-2">
                                    <div class="mr-2">
                                        {{ $t('AI 평가 보기') }}
                                    </div>
                                    <b-form-checkbox v-model="show_ai_assessment" size="lg" name="check-button" switch/>
                                </div>


                                <b-button
                                    variant="dark"
                                    @click="wide_answer_view = !wide_answer_view"
                                >
                                    <fa :icon="wide_answer_view === false ? 'angle-double-right' : 'angle-double-left'"></fa>
                                </b-button>
                            </div>

                        </template>

                        <AIEvaluationReviewWorkspaceV2
                            v-if="show_ai_assessment"
                            :subs="subs"
                            :ev-results="ev_results"
                            :latest-audits="latestAiAuditsBySub"
                            :question-context="aiReviewQuestionContext"
                            :review-identity="aiReviewIdentity"
                            :management-level="aiManagementLevel"
                            @applied="resetSubEvaluation()"
                            @close="show_ai_assessment = false"
                        />

                        <template v-else>
                        <div v-show="wide_answer_view === false"
                             :class="['pb-2 mb-3 position-absolute pt-3', show_ai_assessment ? 'ai-assessment-on' : '']"
                             style="right:15px; top:0;"
                             :style="answer_audit_mode !== 'comment' ? 'width:calc(50% - 30px)' : 'display:none'">
                            <h5 :class="[answer_audit_mode === 'conclusion'
                                ? 'border-bottom-gray-bold gray-500'
                                : 'border-bottom border-color-gray-700 border-width-2 ',
                            ]">
                                상세평가
                            </h5>
                            <b-row v-if="!loading && checkEvResultAvailable">
                                <b-col cols="12">
                                    <b-card no-body class="p-2 mt-2 evaluation-summary">
                                        <b-row no-gutters class="align-items-stretch text-center">
                                            <b-col class="col-2 pr-3 mb-1 mb-md-0">
                                                <strong class="f-95 d-block">평가 요약</strong>
                                            </b-col>
                                            <b-col class="col-auto pr-3 f-90 mb-1 mb-md-0">
                                                <div>총 문항</div>
                                                <b class="d-block mt-1">{{ question_ev_result.total }}</b>
                                            </b-col>
                                            <b-col class="col-auto pr-3 f-90 mb-1 mb-md-0">
                                                <div>매우미흡</div>
                                                <b class="d-block mt-1 text-danger">{{ question_ev_result.D }}</b>
                                            </b-col>
                                            <b-col class="col-auto pr-3 f-90 mb-1 mb-md-0">
                                                <div>미흡</div>
                                                <b class="d-block mt-1">{{ question_ev_result.C }}</b>
                                            </b-col>
                                            <b-col class="col-auto pr-3 f-90 mb-1 mb-md-0">
                                                <div>보통</div>
                                                <b class="d-block mt-1">{{ question_ev_result.B }}</b>
                                            </b-col>
                                            <b-col class="col-auto pr-3 f-90 mb-1 mb-md-0">
                                                <div>우수</div>
                                                <b class="d-block mt-1">{{ question_ev_result.A }}</b>
                                            </b-col>
                                            <b-col class="col-auto pr-3 f-90 mb-1 mb-md-0">
                                                <div>해당없음</div>
                                                <b class="d-block mt-1">{{ question_ev_result.N }}</b>
                                            </b-col>
                                            <b-col class="col-auto pr-3 f-90 mb-1 mb-md-0">
                                                <div>미답변</div>
                                                <b class="d-block mt-1">{{ question_ev_result.none }}</b>
                                            </b-col>
                                        </b-row>
                                        <b-row>
                                            <b-col class="col-2">
                                                <b-badge v-if="mapEvCodeLv[question_ev_result.aggrLv]"
                                                         :variant="get_sub_variant" class="f-100 level-badge px-3 py-2">
                                                    {{ mapEvCodeLv[question_ev_result.aggrLv]?.description }}
                                                </b-badge>
                                            </b-col>
                                            <b-col class="col d-flex align-items-center mt-2 px-4">
                                                <b-progress height="12px" :max="question_ev_result.total"
                                                            class="flex-grow-1">
                                                    <b-progress-bar
                                                        :value="question_ev_result.total - question_ev_result.none"
                                                        variant="success" :title="`답변 ${0}`"/>
                                                    <b-progress-bar :value="question_ev_result.none" variant="secondary"
                                                                    :title="`미답변 ${question_ev_result.none}`"/>
                                                </b-progress>
                                            </b-col>
                                        </b-row>
                                    </b-card>
                                </b-col>
                            </b-row>
                        </div>
                        <br/>
                        <template v-if="!taxonomy_mode">
                            <b-overlay :show="sub_ev_error_data && is_admin >= 3"
                                       rounded="sm"
                                       opacity="0.9"
                                       bg-color="#efefef">
                                <template v-if="subs_loaded && is_question_subs_loaded">
                                    <!-- 관리수준 레벨 위젯 (custom hyundai-v2 이관 — 위젯이 /v2/scoring 자체 조회) -->
                                    <div v-if="show_management_level" class="mb-4">
                                        <ManagementLevelTable :code-id="mlt_current_code_id"
                                                              :company-id="answer_company.company_id"
                                                              :campaign-id="mlt_campaign_id"
                                                              :project-code="$route.query.pc || $route.query.project_code || answer_company.project_code"
                                                              :sub-codes="mlt_sub_codes"
                                                              :subs-data="subs"
                                        />
                                    </div>
                                    <div v-for="(item,idx) in subs"
                                         :key="item.index"
                                         class="pb-5 d-flex justify-content-between"
                                    >
                                        <!-- 기업 답변 영역 -->
                                        <div class="mb-3 p-3" :class="wide_answer_view === true ? 'w-100' : 'w-50'">
                                            <sub-component
                                                :ref="item.sub_code"
                                                :sub_type="item.sub_type"
                                                :item="item"
                                                :is_disabled_by_parent="is_disabled_answer_edit"
                                                :audit_mode="audit_mode"
                                            />
                                        </div>

                                        <!-- 상세평가 영역 -->
                                        <div v-show="wide_answer_view === false" class="mb-3 w-50 p-3"
                                             :style="idx === 0 ? 'margin-top:65px' : ''"
                                        >
                                            <EvaluationComponentV2
                                                :sub_code="item.sub_code"
                                                :sub_id="item.sub_id"
                                                :sub_label="item.label"
                                                :show_ai_assessment="show_ai_assessment"
                                                :sub_description="item.sub_description"
                                                :ev_results="ev_results"
                                                :disabled="answer_audit_mode=='conclusion'"
                                                @reset-sub-evaluation="resetSubEvaluation"
                                            />
                                        </div>
                                    </div>

                                </template>
                                <div v-else-if="question_composition.question_code === ''"
                                     class="py-5 text-center bgray-200 rounded"
                                >
                                    Questions에서 질문명을 클릭하여 평가를 시작하세요
                                </div>
                                <div v-else class="text-center">
                                    <b-spinner class="my-5 gray-500"></b-spinner>
                                </div>

                                <template #overlay>
                                    <div class="bg-white rounded p-4 shadow-lg position-relative"
                                         style="width: 400px; transform: translateX(0) translateY(-100%)">
                                        <!-- 경고 아이콘과 제목 -->
                                        <div class="text-center mb-3">
                                            <b-icon
                                                icon="exclamation-triangle-fill"
                                                variant="danger"
                                                font-scale="3"
                                                class="mb-2"
                                            ></b-icon>
                                            <h4 class="text-danger font-weight-bold"> {{ $t('평가 충돌 감지') }} </h4>
                                        </div>

                                        <!-- 주 경고 메시지 -->
                                        <b-alert show variant="warning" class="mb-3">
                                            <div>
                                                <template v-if="$i18n.locale === 'ko'">
                                                    <strong>사용자의 답변이 변경</strong>되어 수동 평가 내용과 자동 평가 내용에서 충돌이 발생했습니다.
                                                </template>
                                                <template v-else>
                                                    <strong>User's answer has been changed</strong>, causing a conflict
                                                    between manual evaluation and automatic evaluation.
                                                </template>
                                            </div>
                                            <div class="mt-2">
                                                <template v-if="$i18n.locale === 'ko'">
                                                    평가 및 스코어링을 정상적으로 수행하기 위해서 입력된 <strong>평가 내용을 초기화</strong>해야 합니다.
                                                </template>
                                                <template v-else>
                                                    To perform evaluation and scoring correctly, you need to <strong>reset
                                                    the evaluation content</strong> that has been entered.
                                                </template>
                                            </div>
                                        </b-alert>

                                        <!-- 대상 문항 표시 -->
                                        <div class="mb-4">
                                            <h6 class="font-weight-bold text-dark mb-2 text-center">
                                                {{ $t('아래 문항의 평가 내용을 초기화 하시겠습니까?') }}
                                            </h6>
                                            <b-alert show variant="secondary" class="mb-0 py-2">
                                                <strong> {{ $t('대상 문항') }}:</strong>
                                                <div class="d-flex flex-wrap" style="gap: 8px">
                                                    <h5 v-for="ev_error in sub_ev_error_data"
                                                        :key="ev_error.sub_code"
                                                        style="margin-bottom: 0"
                                                    >
                                                        <b-badge> {{ ev_error.label }}</b-badge>
                                                    </h5>
                                                </div>

                                            </b-alert>
                                        </div>

                                        <!-- 버튼 영역 -->
                                        <div class="text-center">
                                            <b-button
                                                variant="danger"
                                                class="mr-3"
                                                @click="resetConflictEvaluation"
                                            >
                                                <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                                                {{ $t('평가 내용 초기화') }}
                                            </b-button>
                                        </div>
                                    </div>
                                </template>
                            </b-overlay>

                        </template>
                        <template v-else>
                            <div class="pb-5 d-flex justify-content-between">
                                <!-- <taxonomy-check class="w-50 mb-3 p-3" :audit_mode="audit_mode" :file_link_key="file_link_key"></taxonomy-check> -->
                                <!-- <audit-taxonomy class="w-50 mb-3 p-3"></audit-taxonomy> -->
                            </div>
                        </template>
                        <div :class="subs_loaded && subs.length > 0 ? 'bg-half-gray100' : ''"></div>
                        <hr class="border-0 bottom-space">
                        </template>
                    </b-col>
                    <template v-if="answer_audit_mode === 'conclusion'">
                        <b-col
                            :class="[answer_audit_mode !== 'conclusion' ? '' : 'opening py-3 pr-0' , 'conclusion-panel-1']">
                            <div class="position-sticky conclusion-sticky pr-3 pb-5">
                                <h5 class="pb-2 mb-3 border-bottom border-color-gray-700 border-width-2"> 평가결론 </h5>
                                <audit-conclusion></audit-conclusion>
                                <hr class="border-0 mb-4">
                            </div>
                        </b-col>
                    </template>
                </b-row>
            </b-container>

            <b-modal id="company-attachements-storage" size="xl" :title="$t('첨부파일 보관함')" hide-footer scrollable>
                <AttachmentsStorage/>
            </b-modal>
        </b-overlay>
    </div>
</template>

<script>


import {mapState, mapActions, mapMutations, mapGetters} from 'vuex';
import subComponent from '@core_components/admin/components/subs/SubComponent.vue';
import AuditPrintButton from '@shared_components/admin/components/common/AuditPrintButton';
import AuditConclusion from '@shared_components/admin/components/audit/AuditConclusion';
import AuditQuestionList from '@shared_components/admin/components/audit/AuditQuestionList.vue';
import AttachmentsStorage from '@shared_components/client/components/AttachmentsStorage';
import {isFieldEmpty} from '@core/utils/subComponent/subComponent';
import ManagementLevelTable from '@shared_components/common/ManagementLevelTable.vue';
import mltCampaignConfig from '@shared/mixin/report/raw-data/code_level_config_hyundai_v2.json';
import EvaluationComponentV2 from '@/components/evaluations/EvaluationComponent_v2.vue';
import AuditPrintButtonV2 from '@/components/print/AuditPrintButton_v2.vue';
import AIEvaluationReviewWorkspaceV2 from '@/components/evaluations/AIEvaluationReviewWorkspace_v2.vue';

const {selectLatestAudit} = require('@/components/evaluations/ai-evaluation-review-presentation.cjs');
const {findQuestionBreadcrumb} = require('@shared_components/admin/components/audit/audit-question-navigation-presentation.cjs');


export default {
    name: 'AnswersV2',
    components: {
        AIEvaluationReviewWorkspaceV2,
        AuditPrintButtonV2,
        ManagementLevelTable,
        EvaluationComponentV2,
        subComponent,
        AuditConclusion,
        AuditQuestionList,
        AttachmentsStorage
    },
    data() {
        return {
            answer_audit_mode: 'evaluation',
            is_printing: false,
            is_allowed_edit_msg: '수정하기',
            is_disabled_answer_edit: true,
            audit_mode: true,
            answer_key: 11314,
            taxonomy_mode: false,
            file_link_key: 38421,
            wide_answer_view: false,

            sub_ev_error_data: null,

            // AI 평가 관련 — AI 평가 관리 화면의 [검토]는 ai_review=1 로 검토 모드를 바로 연다
            show_ai_assessment: this.$route.query.ai_review === '1',
            // [검토]로 문항 없이 들어오면 문항부터 골라야 하므로 목록을 펼쳐 둔다
            aiQuestionSidebarCollapsed: !(this.$route.query.ai_review === '1' && !this.$route.query.question_code),
            loading: false,
            ev_results: {},
        };
    },
    computed: {
        ...mapState(['question_composition', 'template_tree', 'admin_mode', 'campaign', 'is_admin', 'is_question_subs_loaded']),
        ...mapState('admin', ['sub_layout_loading', 'answer_loading']),
        ...mapState('project_code', ['now_project', 'project_companies']),
        ...mapState('questions', ['subs', 'subs_loaded', 'answer_company']),
        ...mapState('industry_scores', ['company_info']),
        ...mapState('industry_campaigns', ['campaigns', 'custom_template', 'custom_template_loaded']),
        ...mapState('evaluations', ['map_ev_lv_variant', 'category3Levels', 'category3IntegrityIssues']),
        ...mapState('ai_assessment', ['aiEvData']),
        ...mapGetters('evaluations', ['mapEvCodeLv', 'checkEvResultAvailable']),
        page_answer_loading() {
            if (this.sub_layout_loading) return false;
            return this.answer_loading;
        },
        is_selected_question() {
            return this.$route.query.question_code != null && this.$route.query.question_code !== '';
        },
        // ── 관리수준 레벨 위젯 (custom hyundai-v2 CompanyAnswer 이관, self-fetch 모드) ──
        mlt_campaign_id() {
            const {cmp, campaign_id} = this.$route.query;
            return cmp ?? campaign_id ?? this.answer_company?.campaign_id;
        },
        mlt_current_code_id() {
            const name = this.question_composition?.question_name || '';
            const match = name.match(/^([A-Z]-\d+-\d+)/);
            return match ? match[1] : '';
        },
        show_management_level() {
            // custom 과 동일한 config 키 기반 게이팅 (렌더러 상수 게이트는 테스트 캠페인을 놓친다)
            return !!this.mlt_current_code_id
                && !!mltCampaignConfig[String(this.mlt_campaign_id || '')]
                && !!this.answer_company?.company_id;
        },
        mlt_sub_codes() {
            if (!this.subs || this.subs.length === 0) return [];
            return this.subs.map(sub => sub.sub_code).filter(Boolean);
        },
        // AI 검토 화면용 관리수준 위젯 props. 위젯은 최초 1회만 계산하므로 문항 로딩 중엔 null 로 내려 문항 전환 시 새로 만들게 한다
        aiManagementLevel() {
            if (!this.show_management_level || !this.subs_loaded || !this.is_question_subs_loaded) return null;
            return {
                codeId: this.mlt_current_code_id,
                companyId: this.answer_company.company_id,
                campaignId: this.mlt_campaign_id,
                projectCode: this.$route.query.pc || this.$route.query.project_code || this.answer_company.project_code,
                subCodes: this.mlt_sub_codes,
                subsData: this.subs,
            };
        },
        aiReviewQuestionContext() {
            return findQuestionBreadcrumb(
                this.template_tree?.template_contents,
                this.$route.query.question_code,
                this.$i18n.locale,
            );
        },
        aiReviewIdentity() {
            return {
                companyName: this.answer_company?.company_name ?? '',
                companyId: this.answer_company?.company_id ?? '',
                campaignName: this.answer_company?.campaign_name ?? '',
                campaignId: this.answer_company?.campaign_id ?? '',
            };
        },
        latestAiAuditsBySub() {
            return this.subs.reduce((latest, sub) => {
                if (sub.sub_id == null) return latest;
                const audits = Object.values(
                    this.aiEvData?.[sub.sub_id] || this.aiEvData?.[String(sub.sub_id)] || {},
                );
                latest[sub.sub_id] = selectLatestAudit(audits);
                return latest;
            }, {});
        },
        question_ev_result() {
            const {question_code} = this.$route.query;
            const cnt = {
                total: 0,
                aggrLv: this.category3Levels[question_code],
                none: 0,
            };

            if (this.mapEvCodeLv == null) return cnt;

            Object.keys(this.mapEvCodeLv).forEach(code => {
                cnt[code] = 0;
            });

            Object.values(this.ev_results).forEach(item => {
                if (item.ev_code) {
                    cnt[item.ev_code] += 1;
                    cnt.total += 1;
                }
            });

            cnt.none = this.subs.length - 2 - cnt.total;
            cnt.total += cnt.none;

            return cnt;
        },
        get_sub_variant() {
            return this.map_ev_lv_variant[this.question_ev_result.aggrLv]?.variant || 'secondary';
        }
    },
    watch: {
        project_companies: {
            deep: true,
            immediate: true,
            async handler() {
                if (this.admin_mode !== 'project')
                    return;

                if ((this.project_companies?.length ?? 0) > 0) {
                    const target = this.project_companies?.find(({company_id}) => company_id.toString() === this.$route.params.company_id.toString());
                    if (target) {
                        await this.update_answer_company(target);
                    }
                }
                if (this.answer_company?.company_id) {
                    await this.initData();
                }
            }
        },
        campaigns: {
            deep: true,
            immediate: true,
            async handler() {
                if (this.admin_mode !== 'camp')
                    return;
                if (this.admin_mode === 'camp' && (this.campaigns?.length ?? 0) > 0) {
                    const target = this.campaigns[0]?.scoring_target_companies?.find(({company_id}) => company_id.toString() === this.$route.params.company_id.toString());
                    if (target) {
                        await this.update_answer_company(target);

                    }
                }
                if (this.answer_company?.company_id) {
                    await this.initData();
                }
            }
        },
        question_composition: {
            deep: true,
            async handler(_updated) {
                this.loading = true;
                try {
                    this.$store.commit('update_is_question_subs_loaded', false);
                    if (_updated.question_code && _updated.question_contents) {
                        await this.resetSubEvaluation(_updated);
                        await this.fetchAiEvDataWrap();
                    } else {
                        this.reset_subs(); //이전에 보던 기업의 답변이 있다면 subs를 초기화
                        this.update_subs_loaded(false);
                        this.set_evaluation_list({});
                        this.$store.commit('score/update_overall_comment', {});
                        this.$store.commit('questions/update_subs', []);
                        this.ev_results = {};
                    }
                } catch (e) {
                    // ignore
                } finally {
                    this.loading = false;

                }
            }
        }
    },
    created() {
        if (this.$route.query.admin_mode == null) {
            this.$showModal({
                type: 'alert',
                state: 'warning',
                title: this.$t('잘못된 접근'), // content는 기본값 사용, title만 변경
                message: this.$t('URL에 필요한 정보가 부족합니다. 개발팀에 문의해주세요.'),
            });
            this.$router.push({name: 'ProjectCodeList', params: {urlError: true}});
        }


    },
    mounted() {
        window.addEventListener('keydown', this.toggleShowAIAssessment);
        this.fetchEvCode();
    },
    beforeDestroy() {
        this.answer_audit_mode = 'evaluation';
        this.update_answer_company({});
        this.update_subs_loaded(false);
        this.$store.commit('update_question_composition', {});
        this.is_company_selected = false;
        this.clearPrevAttachments();
        window.removeEventListener('keydown', this.toggleShowAIAssessment);
    },
    methods: {
        ...mapActions(['readTemplateTree', 'makeTemplateTreeByCompanyId']),
        ...mapActions('industry_scores', ['readCompanyInfo', 'readQeEvaluateResult']),
        ...mapActions('industry_campaigns', ['readCustomTemplate']),
        ...mapActions('reviews', ['initiateReviews']),
        ...mapActions('score', ['readAllEvaluateResult']),
        ...mapActions('attachments', ['getAttatchments', 'clearPrevAttachments']),
        ...mapActions('admin', ['setLayoutLoading', 'setAnswerLoading']),
        ...mapActions('evaluations', ['fetchEvList', 'fetchEvResults', 'fetchEvCode', 'fetchCategory3Aggr']),
        ...mapActions('ai_assessment', ['fetchAiEvData', 'setAiEvData']),
        ...mapMutations(['update_question_code', 'update_question_composition']),
        ...mapMutations('evaluations', ['set_evaluation_list']),
        ...mapMutations('questions', ['reset_subs', 'update_answer_company', 'change_edit_mode', 'update_subs_loaded']),
        ...mapMutations('industry_scores', ['reset_industry_scores']),
        ...mapMutations('industry_campaigns', ['update_custom_project_code']),
        ...mapMutations('attachments', ['initAttachmentsStorage']),
        async initData() {
            const {company_id} = this.$route.params;
            let {cmp, campaign_id, template_code, admin_mode, pc, project_code, question_code} = this.$route.query;
            const _project_code = pc ?? project_code;
            const _campaign_id = cmp ?? campaign_id;

            if (isFieldEmpty(template_code)) {
                const targetData = this.project_companies.find(item => item.company_id.toString() === company_id.toString());
                if (targetData && targetData.template_code) {
                    template_code = targetData.template_code;

                    this.$router.push({
                        name: this.$route.name,
                        query: {
                            ...this.$route.query,
                            template_code: template_code
                        }
                    });
                }
            }

            if (isFieldEmpty(_campaign_id) || isFieldEmpty(company_id) || isFieldEmpty(template_code)) {
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: this.$t('잘못된 접근'), // content는 기본값 사용, title만 변경
                    message: this.$t('URL에 필요한 정보가 부족합니다. 개발팀에 문의해주세요.'),
                });
                if (admin_mode === 'project' && isFieldEmpty(_project_code)) {
                    this.$router.push({name: 'ProjectCodeList', params: {urlError: true}});
                } else if (admin_mode === 'camp') {
                    this.$router.push({name: 'CampaignList', params: {urlError: true}});
                } else {
                    this.$router.push({name: 'ProjectCodeList', params: {urlError: true}});
                }
                return;
            }

            try {
                this.setAnswerLoading(true);

                if (this.is_admin < 3) {
                    this.wide_answer_view = true;
                }
                this.clearPrevAttachments();
                this.initAttachmentsStorage();

                await Promise.all([
                    this.readCompanyInfo(company_id),
                    this.readTemplateTree({template_code, is_previous: false, company_id}),
                    this.makeTemplateTreeByCompanyId({campaign_id: _campaign_id, company_id}),
                    this.getAttatchments({campaign_id: _campaign_id, company_id}),
                    this.initiateReviews({campaign_id: _campaign_id, company_id}),
                    this.fetchCategory3Aggr({
                        project_code: _project_code,
                        company_id: Number(company_id),
                    })
                ]);

                if (question_code) {
                    this.$EventBus.$emit('Answers-pipeline_overlay_question_clicked', {
                        question_code
                    });
                }
            } catch (e) {
                // ignore
            } finally {
                this.setAnswerLoading(false);
            }
        },
        async resetSubEvaluation(question = this.question_composition) {
            this.sub_ev_error_data = null;
            let sub_ev_error_data = null;


            try {
                this.$store.commit('update_is_question_subs_loaded', false);
                await Promise.all([
                    
                    this.fetchEvList({question_code: question.question_code}),
                    this.$store.dispatch('score/readOverallComment', {
                        campaign_id: this.answer_company.campaign_id,
                        company_id: this.answer_company.company_id,
                        question_code: question.question_contents
                    }),

                    this.$store.dispatch('questions/readSubs', {
                        campaign_id: this.answer_company.campaign_id,
                        company_id: this.answer_company.company_id,
                        company_name: this.answer_company.company_name,
                        code: question.question_code,
                        contents: question.question_contents
                    })
                ]);
                const {mapEv} = await this.fetchEvResults({
                    campaign_id: this.answer_company.campaign_id,
                    company_id: this.answer_company.company_id,
                    sub_code: question.question_contents,
                });
                this.ev_results = mapEv;
                this.$store.commit('update_is_question_subs_loaded', true);
                this.sub_ev_error_data = sub_ev_error_data;
            } catch (error) {
                console.dir(error);
                alert('평가 및 기업 답변 데이터를 불러오는 도중 장애가 발생했습니다. 개발팀에 문의해주세요.');
            }
        },
        returnToSelect() {
            this.$router.push({name: 'Answers', query: {...this.$route.query}});
            this.update_question_code('');
            this.update_answer_company({});
            this.reset_industry_scores(true);
        },
        changeMode(mode) {
            this.answer_audit_mode = mode;
        },
        toggleShowAIAssessment(e) {
            if ((e.key === 'a' || e.key === 'A') && e.altKey) {
                this.show_ai_assessment = !this.show_ai_assessment;
            }
        },
        toggleAllowedEdit() {
            this.is_disabled_answer_edit = !this.is_disabled_answer_edit;
            this.audit_mode = !this.audit_mode;
            if (this.is_disabled_answer_edit == false) {
                this.is_allowed_edit_msg = '수정완료';
            } else {
                this.is_allowed_edit_msg = '수정하기';
            }
            this.answer_key += 1;
        },
        showOrHideTaxonomy(mode) {
            this.file_link_key += 1;
            this.taxonomy_mode = mode == 'open';
        },
        async fetchAiEvDataWrap() {
            const sub_ids = this.subs.reduce((acc, cur) => {
                if (cur.sub_id != null) {
                    acc.push(Number(cur.sub_id));
                }
                return acc;
            }, []);
            const aiEvData = await this.fetchAiEvData({sub_id: sub_ids});
            await this.setAiEvData(aiEvData);
        },
        async resetConflictEvaluation() {
            this.setAnswerLoading(true);

            try {
                const sub_codes = this.sub_ev_error_data.map(ev => ev.sub_code);

                await this.$axios.delete('/delete_audit_by_sub_codes', {
                    data: {
                        campaign_id: this.$route.query.cmp,
                        company_id: this.$route.query.cpn,
                        sub_codes,
                        confirm: true,
                    }
                });

                this.$EventBus.$emit('toast-success', this.$t('평가 내용 초기화'), this.$t('평가 내용이 성공적으로 초기화되었습니다.'));

                await this.resetSubEvaluation();
            } catch (e) {
                console.error('평가 내용 초기화 중 오류 발생:', e);
                console.dir('평가 내용 초기화 중 오류 발생:', e);
                this.$showModal({
                    type: 'alert',
                    state: 'error',
                    title: this.$t('오류 발생'),
                    message: this.$t('평가 내용 초기화 중 오류가 발생했습니다. 개발팀에 문의해주세요.')
                });

            } finally {
                this.setAnswerLoading(false);
            }
        },
    }
};
</script>
<style>
.bg-half-gray100 {
    position: absolute;
    width: 50%;
    height: 100%;
    top: 0;
    left: 0;
    background-color: var(--gray-100);
    z-index: -1;
}

.conclusion-sticky {
    top: 70px;
    height: calc(100vh - 70px);
    overflow: auto;
}

.ev-container {
    .ai-evaluation-container {
        margin-top: -41px;
    }

    > *:not(:first-child) {
        .ai-evaluation-container {
        }
    }
}

.ai-assessment-on {
    margin-top: -17px
}

.ai-refresh-btn {
    cursor: pointer;
    transition: color 0.3s ease;
    margin-right: 12px;
}

.evaluation-summary {
    background: #f8f9fa;
    border: 1px solid #e2e6ea;
    border-radius: 4px;

    .progress {
        min-width: 160px;
    }
}

.ai-review-page {
    padding-right: 0 !important;
    padding-left: 0 !important;
}

.ai-review-page > .row {
    margin-right: 0;
    margin-left: 0;
    flex-wrap: nowrap;
}

.ai-question-sidebar {
    flex: 0 0 16.666667%;
    max-width: 16.666667%;
    min-width: 0;
    padding-right: 1rem;
    padding-left: 1rem;
    transition: flex-basis 0.16s ease, max-width 0.16s ease;
}

.ai-question-sidebar.is-compact {
    flex-basis: 48px;
    max-width: 48px;
    padding-right: 8px;
    padding-left: 8px;
}

.questions-sticky {
    top: 70px;
    max-height: calc(100vh - 70px);
    overflow-y: auto;
}

.questions-heading {
    min-height: 33px;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid var(--gray-500);
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.questions-heading.is-compact {
    justify-content: center;
    margin-bottom: 5px;
    padding-bottom: 4px;
}

.questions-collapse-button {
    min-width: 24px;
    padding: 2px 4px;
    color: var(--gray-600);
}

.ai-review-main {
    flex: 1 1 0;
    width: 0;
    max-width: none;
    min-width: 0;
}

.ai-review-host {
    padding-right: 0 !important;
    padding-left: 0 !important;
    background: #fff !important;
}
</style>
